import React, { useEffect, useMemo, useState, useRef } from 'react';
import { FaPaperPlane, FaPaperclip, FaLink } from 'react-icons/fa';
import { extractEmbedFromContent, getEmbedSrc } from '../../utils/embed';
import { useHeaderActions } from '../../contexts/HeaderActionsContext.jsx';
import './DockedComposer.css';

/**
 * DockedComposer - A reusable sticky launcher + modal composer for social posts.
 * 
 * Props:
 * - placeholder: string - Placeholder text for the textarea
 * - onSubmit: (content, mediaFile, mediaType) => Promise<void> - Called when user submits
 * - isSubmitting: boolean - Whether a submission is in progress
 * - title: string - Title shown in the composer header (default: "Create Post")
 */
const DockedComposer = ({
  placeholder = "Share something...",
  onSubmit,
  isSubmitting = false,
  title = "Create Post",
  showDesktopLauncher = false,
  registerHeaderLauncher = true
}) => {
  const [composerOpen, setComposerOpen] = useState(false);
  const [content, setContent] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [localEmbedPreview, setLocalEmbedPreview] = useState(null);
  const fileInputRef = useRef();

  const { setLeftSlot } = useHeaderActions();

  const headerLauncher = useMemo(() => (
    <button
      type="button"
      className="auth-header-launcher-button"
      title={title}
      aria-label={title}
      onClick={() => setComposerOpen(true)}
    >
      <FaPaperPlane />
    </button>
  ), [title]);

  useEffect(() => {
    if (!registerHeaderLauncher) return;
    setLeftSlot(headerLauncher);
    return () => setLeftSlot(null);
  }, [headerLauncher, registerHeaderLauncher, setLeftSlot]);

  const handleContentChange = (e) => {
    const value = e.target.value;
    setContent(value);
    const { embedUrl } = extractEmbedFromContent(value);
    setLocalEmbedPreview(embedUrl ? getEmbedSrc(embedUrl) : null);
  };

  const handleMediaSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setMediaFile(file);
      setMediaPreview(URL.createObjectURL(file));
      setMediaType(
        file.type.startsWith('video') ? 'video' :
        file.type.startsWith('image') ? 'image' : 'file'
      );
    }
  };

  const handleRemoveMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
  };

  const handleSubmit = async () => {
    if (!content.trim() && !mediaFile && !localEmbedPreview) return;
    
    try {
      await onSubmit(content, mediaFile, mediaType, localEmbedPreview);
      // Reset form on success
      setContent('');
      setMediaFile(null);
      setMediaPreview(null);
      setMediaType(null);
      setLocalEmbedPreview(null);
      setComposerOpen(false);
    } catch (err) {
      // Let parent handle error display
      console.error('DockedComposer submit error:', err);
    }
  };

  const canSubmit = !isSubmitting && (content.trim() || mediaFile || localEmbedPreview);

  return (
    <>
      {showDesktopLauncher && (
        <div className="composer-launcher-gutter">
          <button
            className="composer-launcher-btn"
            title={title}
            aria-label={title}
            onClick={() => setComposerOpen(true)}
          >
            <FaPaperPlane />
          </button>
        </div>
      )}

      {/* Mobile Floating Action Button */}
      <button
        className="mobile-composer-fab composer-launcher-btn"
        title={title}
        aria-label={title}
        onClick={() => setComposerOpen(true)}
      >
        <FaPaperPlane />
      </button>

      {/* Docked Composer Modal */}
      {composerOpen && (
        <div className="docked-composer-overlay" onClick={() => setComposerOpen(false)}>
          <div className="docked-composer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="docked-composer-header">
              <span className="docked-composer-title">{title}</span>
              <button
                className="docked-composer-close"
                onClick={() => setComposerOpen(false)}
                aria-label="Close composer"
              >
                ✕
              </button>
            </div>
            <div className="docked-composer-body">
              <textarea
                className="social-composer-input"
                placeholder={placeholder}
                value={content}
                onChange={handleContentChange}
                autoFocus
              />
              {localEmbedPreview && !mediaPreview && (
                <div className="social-media-preview">
                  <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
                    <iframe
                      src={localEmbedPreview}
                      title="Preview"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                      allowFullScreen
                    />
                  </div>
                </div>
              )}
              {mediaPreview && (
                <div className="social-media-preview">
                  {mediaType === 'video' ? (
                    <video controls src={mediaPreview} />
                  ) : mediaType === 'image' ? (
                    <img src={mediaPreview} alt="attachment" />
                  ) : (
                    <a href={mediaPreview} target="_blank" rel="noreferrer">Attachment</a>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      className="icon-btn"
                      title="Remove media"
                      aria-label="Remove media"
                      onClick={handleRemoveMedia}
                    >
                      ✖
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="docked-composer-actions">
              <button
                type="button"
                className="social-icon-btn icon-only"
                title="Attach media"
                aria-label="Attach media"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting}
              >
                <FaPaperclip />
              </button>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*,video/*"
                onChange={handleMediaSelect}
              />
              <button
                type="button"
                className="social-icon-btn icon-only"
                title="Paste a YouTube/SoundCloud link in the text above to embed"
                aria-label="Embeds are detected from text"
                disabled
              >
                <FaLink />
              </button>
              <button
                className="social-submit-btn"
                title="Post"
                aria-label="Post"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                <FaPaperPlane /> Post
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DockedComposer;
